/* Wrapper for common router calls */
import VueRouter from 'vue-router'
import * as routerNames from '@/services/router/router.names'
import Permissions from '@/plugins/permissions/permissions'
import { DefaultFilterIDs } from '@/types'
import LocalStore from '@/helpers/localstore'

export default new class RouteAssistant {
  private openInNewWindow(router: VueRouter, route: string) {
    const routeData = router.resolve(route)
    window.open(routeData.href, '_blank')
  }

  /* Go to routes */
  public goToAccountRegistrationComplete(
    router: VueRouter,
    donedoneToken: string,
    gAuthToken: string,
    gl: string,
  ): void {
    router.push(
      this.getAccountRegistrationCompleteRoute(donedoneToken, gAuthToken, gl),
    )
  }

  public goToDashboard(router: VueRouter, accountId: Number | string): void {
    router.push(this.getDashboardRoute(accountId))
  }

  public goToAllConversationsWithDefaultFilter(
    router: VueRouter,
    accountId: Number | string,
    defaultFilterId: number | string,
  ): void {
    router.push(this.getAllConversationsRoute(accountId, defaultFilterId, null))
  }

  public goToAllConversationsWithCustomFilter(
    router: VueRouter,
    accountId: Number | string,
    customFilterId: number | string,
  ): void {
    router.push(this.getAllConversationsRoute(accountId, null, customFilterId))
  }

  public goToAllTasksWithDefaultFilter(
    router: VueRouter,
    accountId: Number | string,
    defaultFilterId: number | string,
  ): void {
    router.push(this.getAllTasksRoute(accountId, defaultFilterId, null))
  }

  public goToAllTasksWithCustomFilter(
    router: VueRouter,
    accountId: Number | string,
    customFilterId: number | string,
  ): void {
    router.push(this.getAllTasksRoute(accountId, null, customFilterId))
  }

  public goToMailbox(
    router: VueRouter,
    accountId: Number | string,
    mailboxId: Number | string,
    title?: string | null,
  ): void {
    router.push(this.getMailboxRoute(accountId, mailboxId, title))
  }

  public goToMailboxWithDefaultFilter(
    router: VueRouter,
    accountId: Number | string,
    mailboxId: Number | string,
    defaultFilterId: number | string,
  ): void {
    router.push(
      this.getMailboxRoute(accountId, mailboxId, null, defaultFilterId),
    )
  }

  public goToMailboxWithCustomFilter(
    router: VueRouter,
    accountId: Number | string,
    mailboxId: Number | string,
    customFilterId: number | string,
  ): void {
    router.push(
      this.getMailboxRoute(accountId, mailboxId, null, null, customFilterId),
    )
  }

  // Routes to either project listing or kanban based on last visit...
  public goToProjectLanding(
    router: VueRouter,
    accountId: number | string,
    projectId: number | string,
    permissions: Permissions,
    title?: string | null,
  ): void {
    router.push(
      this.getProjectLandingRoute(accountId, projectId, permissions, title),
    )
  }

  public goToProjectListing(
    router: VueRouter,
    accountId: Number | string,
    projectId: Number | string,
    permissions: Permissions,
    title?: string | null,
  ): void {
    router.push(
      this.getProjectListingRoute(accountId, projectId, permissions, title),
    )
  }

  public goToProjectListingWithDefaultFilter(
    router: VueRouter,
    accountId: Number | string,
    projectId: Number | string,
    permissions: Permissions,
    defaultFilterId: number | string,
  ): void {
    router.push(
      this.getProjectListingRoute(
        accountId,
        projectId,
        permissions,
        null,
        defaultFilterId,
      ),
    )
  }

  public goToProjectListingWithCustomFilter(
    router: VueRouter,
    accountId: Number | string,
    projectId: Number | string,
    permissions: Permissions,
    customFilterId: number | string,
  ): void {
    router.push(
      this.getProjectListingRoute(
        accountId,
        projectId,
        permissions,
        null,
        null,
        customFilterId,
      ),
    )
  }

  public goToProjectKanban(
    router: VueRouter,
    accountId: Number | string,
    projectId: Number | string,
    permissions: Permissions,
    title?: string | null,
  ): void {
    router.push(
      this.getProjectKanbanRoute(accountId, projectId, permissions, title),
    )
  }

  public goToProjectKanbanWithDefaultFilter(
    router: VueRouter,
    accountId: Number | string,
    projectId: Number | string,
    permissions: Permissions,
    defaultFilterId: number | string,
  ): void {
    router.push(
      this.getProjectKanbanRoute(
        accountId,
        projectId,
        permissions,
        null,
        defaultFilterId,
      ),
    )
  }

  public goToProjectKanbanWithCustomFilter(
    router: VueRouter,
    accountId: Number | string,
    projectId: Number | string,
    permissions: Permissions,
    customFilterId: number | string,
  ): void {
    router.push(
      this.getProjectKanbanRoute(
        accountId,
        projectId,
        permissions,
        null,
        null,
        customFilterId,
      ),
    )
  }

  public goToConversation(
    router: VueRouter,
    accountId: Number | string,
    mailboxId: Number | string,
    conversationId: Number | string,
    mailboxName?: string | null,
    refNumber?: Number | string | null,
    title?: string | null,
    newWindow?: boolean,
    eventId?: Number | string | null,
    fromAll?: boolean | string | null,
    fromMailboxHome?: boolean | string | null,
  ): void {
    const route = this.getConversationRoute(
      accountId,
      mailboxId,
      conversationId,
      mailboxName,
      refNumber,
      title,
      eventId,
      fromAll,
      fromMailboxHome,
    )

    if (newWindow) {
      this.openInNewWindow(router, route)
    } else {
      router.push(route)
    }
  }

  public goToTask(
    router: VueRouter,
    accountId: Number | string,
    projectId: Number | string,
    taskId: Number | string,
    permissions: Permissions,
    projectName?: string | null,
    refNumber?: Number | string | null,
    title?: string | null,
    newWindow?: boolean,
    eventId?: Number | string | null,
    fromAll?: boolean | string | null,
    fromListing?: boolean | string | null,
    fromKanbanBoard?: boolean | string | null,
  ): void {
    const route = this.getTaskRoute(
      accountId,
      projectId,
      taskId,
      permissions,
      projectName,
      refNumber,
      title,
      eventId,
      fromAll,
      fromListing,
      fromKanbanBoard,
    )

    if (newWindow) {
      this.openInNewWindow(router, route)
    } else {
      router.push(route)
    }
  }

  public goToTaskFromKanbanBoard(
    router: VueRouter,
    accountId: Number | string,
    projectId: Number | string,
    taskId: Number | string,
    permissions: Permissions,
    refNumber?: Number | string | null,
  ): void {
    const route = this.getTaskRoute(
      accountId,
      projectId,
      taskId,
      permissions,
      null,
      refNumber,
      null,
      null,
      null,
      null,
      true,
    )
    router.push(route)
  }

  public goToCreateConversation(
    router: VueRouter,
    accountId: Number | string,
    mailboxId: Number | string,
    mailboxName?: string | null,
  ): void {
    router.push({
      name: routerNames.CONVERSATION_NEW,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
        mailboxName,
      },
    })
  }

  public goToCreateTask(
    router: VueRouter,
    accountId: Number | string,
    projectId: Number | string,
    permissions: Permissions,
    projectName?: string | null,
  ): void {
    const name: string = permissions.isSampleInternalProjectID(projectId)
      ? routerNames.SAMPLE_TASK_NEW
      : routerNames.TASK_NEW

    router.push({
      name,
      params: {
        accountId: accountId.toString(),
        id: projectId.toString(),
        projectName,
      },
    })
  }

  public goToAllTasks(router: VueRouter, accountId: Number | string): void {
    router.push({
      name: routerNames.ALL_TASKS,
      params: {
        accountId: accountId.toString(),
      },
    })
  }

  public goToAllConversations(
    router: VueRouter,
    accountId: Number | string,
  ): void {
    router.push({
      name: routerNames.ALL_CONVERSATIONS,
      params: {
        accountId: accountId.toString(),
      },
    })
  }

  public goToWorkflowsHome(
    router: VueRouter,
    accountId: Number | string,
  ): void {
    router.push(this.getWorkflowsRoute(accountId))
  }

  public goToWorkflowDetail(
    router: VueRouter,
    accountId: Number | string,
    workflowId: Number | string,
  ): void {
    router.push(this.getWorkflowDetailRoute(accountId, workflowId))
  }

  public goToCalendar(
    router: VueRouter,
    accountId: Number | string,
    entityId?: Number | string,
  ): void {
    router.push(this.getCalendarRoute(accountId, entityId))
  }

  public goToReports(
    accountId: Number | string,
    entityId?: Number | string,
  ): void {
    this.goToUrl(this.getReportsUrl(accountId, entityId))
  }

  public goToDevelopersHome(
    router: VueRouter,
    accountId: Number | string,
  ): void {
    router.push(this.getDevelopersHomeRoute(accountId))
  }

  public goToOutboundWebhooks(
    router: VueRouter,
    accountId: Number | string,
  ): void {
    router.push(this.getOutboundWebhooksRoute(accountId))
  }

  public goToTicketPortalListing(
    router: VueRouter,
    accountId: Number | string,
    mailboxId: Number | string,
  ): void {
    router.push(this.getTicketPortalListingRoute(accountId, mailboxId))
  }

  public goToTicketPortalDetail(
    router: VueRouter,
    accountId: Number | string,
    mailboxId: Number | string,
    ticketId: Number | string,
  ): void {
    router.push(this.getTicketPortalDetailRoute(accountId, mailboxId, ticketId))
  }

  public goToPeople(router: VueRouter, accountId: Number | string): void {
    router.push(this.getPeopleRoute(accountId))
  }

  public goToGuests(router: VueRouter, accountId: Number | string): void {
    router.push(this.getGuestsRoute(accountId))
  }

  public goToProfile(accountId: Number | string): void {
    this.goToUrl(this.getProfileUrl(accountId))
  }

  public goToAccountSettings(accountId: Number | string): void {
    this.goToUrl(this.getAccountSettingsUrl(accountId))
  }

  public goToBillingInformation(accountId: Number | string): void {
    this.goToUrl(this.getBillingInformationUrl(accountId))
  }

  public goToSignup(): void {
    this.goToUrl(this.getSignupUrl())
  }

  public goToLogin(): void {
    this.goToUrl(this.getLoginUrl())
  }

  public goToBase(): void {
    this.goToUrl(this.getBaseUrl())
  }

  public goToPersonDetail(
    router: VueRouter,
    accountId: Number | string,
    accountMembershipId: Number | string,
  ): void {
    router.push(this.getPersonDetailRoute(accountId, accountMembershipId))
  }

  public goToGuestsDetail(
    router: VueRouter,
    accountId: Number | string,
    accountMembershipId: Number | string,
  ): void {
    router.push(this.getGuestsDetailRoute(accountId, accountMembershipId))
  }

  public goToCompanyDetail(
    router: VueRouter,
    accountId: Number | string,
    companyId: Number | string,
  ): void {
    router.push(this.getCompanyDetailRoute(accountId, companyId))
  }

  public goToCompanyGuestsDetail(
    router: VueRouter,
    accountId: Number | string,
    companyId: Number | string,
  ): void {
    router.push(this.getCompanyGuestsDetailRoute(accountId, companyId))
  }

  public goToCreateProject(
    router: VueRouter,
    accountId: Number | string,
    workflowId?: Number | string,
  ): void {
    router.push(this.getCreateProjectRoute(accountId, workflowId))
  }

  public goToCreateMailbox(
    router: VueRouter,
    accountId: Number | string,
    workflowId?: Number | string,
  ): void {
    router.push(this.getCreateMailboxRoute(accountId, workflowId))
  }

  public goToGlobalCreateTask(
    router: VueRouter,
    accountId: Number | string,
  ): void {
    router.push(this.getGlobalCreateTaskRoute(accountId))
  }

  public goToCreatePeople(
    router: VueRouter,
    accountId: Number | string,
    companyId?: Number | string,
  ): void {
    router.push(this.getCreatePeopleRoute(accountId, companyId))
  }

  public goToCreateGuests(
    router: VueRouter,
    accountId: Number | string,
    companyId?: Number | string,
  ): void {
    router.push(this.getCreateGuestsRoute(accountId, companyId))
  }

  public goToCreateCompany(
    router: VueRouter,
    accountId: Number | string,
  ): void {
    router.push(this.getCreateCompanyRoute(accountId))
  }

  public goToCreateGuestsCompany(
    router: VueRouter,
    accountId: Number | string,
  ): void {
    router.push(this.getCreateGuestsCompanyRoute(accountId))
  }

  public goToMailboxVerificationIntroRoute(
    router: VueRouter,
    accountId: Number | string,
    mailboxId: Number | string,
  ): void {
    router.push(this.getMailboxVerificationIntroRoute(accountId, mailboxId))
  }

  public goToMailboxVerificationAutoForwardingRoute(
    router: VueRouter,
    accountId: Number | string,
    mailboxId: Number | string,
  ): void {
    router.push(
      this.getMailboxVerificationAutoForwardingRoute(accountId, mailboxId),
    )
  }

  public goToMailboxVerificationAutoForwardingTestingRoute(
    router: VueRouter,
    accountId: Number | string,
    mailboxId: Number | string,
  ): void {
    router.push(
      this.getMailboxVerificationAutoForwardingTestingRoute(
        accountId,
        mailboxId,
      ),
    )
  }

  public goToMailboxVerificationOutgoingEmailRoute(
    router: VueRouter,
    accountId: Number | string,
    mailboxId: Number | string,
  ): void {
    router.push(
      this.getMailboxVerificationOutgoingEmailRoute(accountId, mailboxId),
    )
  }

  public goToMailboxSettingsAutoForwardingRoute(
    router: VueRouter,
    accountId: Number | string,
    mailboxId: Number | string,
  ): void {
    router.push(
      this.getMailboxSettingsAutoForwardingRoute(accountId, mailboxId),
    )
  }

  public goToMailboxSettingsOfficeHoursRoute(
    router: VueRouter,
    accountId: Number | string,
    mailboxId: Number | string,
  ): void {
    router.push(this.getMailboxSettingsOfficeHoursRoute(accountId, mailboxId))
  }

  public goToMailboxSettingsOutgoingEmailRoute(
    router: VueRouter,
    accountId: Number | string,
    mailboxId: Number | string,
  ): void {
    router.push(this.getMailboxSettingsOutgoingEmailRoute(accountId, mailboxId))
  }

  public goToMailboxSettingsTicketPortalRoute(
    router: VueRouter,
    accountId: Number | string,
    mailboxId: Number | string,
  ): void {
    router.push(this.getMailboxSettingsTicketPortalRoute(accountId, mailboxId))
  }

  public goToMailboxSettingsAssignPeopleRoute(
    router: VueRouter,
    accountId: Number | string,
    mailboxId: Number | string,
  ): void {
    router.push(this.getMailboxSettingsAssignPeopleRoute(accountId, mailboxId))
  }

  public goToProjectSettingsAssignPeopleRoute(
    router: VueRouter,
    accountId: Number | string,
    projectId: Number | string,
  ): void {
    router.push(this.getProjectSettingsAssignPeopleRoute(accountId, projectId))
  }

  public goToUrl(url: string, external?: boolean): void {
    if (external) {
      window.open(url)
    } else {
      window.location.href = url
    }
  }

  /* Routes */
  public getDashboardRoute(accountId: Number | string): any {
    return {
      name: routerNames.DASHBOARD,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getAllTasksRoute(
    accountId: Number | string,
    defaultFilterId?: number | string | null,
    customFilterId?: number | string | null,
  ) {
    const query: any = defaultFilterId
      ? {
          default_filter_id: defaultFilterId,
        }
      : customFilterId
        ? {
            custom_filter_id: customFilterId,
          }
        : {}
    return {
      name: routerNames.ALL_TASKS,
      params: {
        accountId: accountId.toString(),
      },
      query,
    }
  }

  public getAllConversationsRoute(
    accountId: Number | string,
    defaultFilterId?: number | string | null,
    customFilterId?: number | string | null,
  ) {
    const query: any = defaultFilterId
      ? {
          default_filter_id: defaultFilterId,
        }
      : customFilterId
        ? {
            custom_filter_id: customFilterId,
          }
        : {}
    return {
      name: routerNames.ALL_CONVERSATIONS,
      params: {
        accountId: accountId.toString(),
      },
      query,
    }
  }

  public getMailboxRoute(
    accountId: Number | string,
    mailboxId: Number | string,
    title?: string | null,
    defaultFilterId?: number | string | null,
    customFilterId?: number | string | null,
  ): any {
    const titleVal: string = title ? title : ''
    const query: any = defaultFilterId
      ? {
          default_filter_id: defaultFilterId,
        }
      : customFilterId
        ? {
            custom_filter_id: customFilterId,
          }
        : {}

    return {
      name: routerNames.MAILBOX_HOME,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
        title: titleVal,
      },
      query,
    }
  }

  public getMailboxSettingsAutoForwardingRoute(
    accountId: Number | string,
    mailboxId: Number | string,
    title?: string | null,
  ): any {
    const titleVal: string = title ? title : ''

    return {
      name: routerNames.MAILBOX_SETTINGS_AUTO_FORWARDING,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
        title: titleVal,
      },
      query: {},
    }
  }

  public getMailboxSettingsAutoResponderRoute(
    accountId: Number | string,
    mailboxId: Number | string,
    title?: string | null,
  ): any {
    const titleVal: string = title ? title : ''

    return {
      name: routerNames.MAILBOX_SETTINGS_AUTO_RESPONDER,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
        title: titleVal,
      },
      query: {},
    }
  }

  public getMailboxSettingsOfficeHoursRoute(
    accountId: Number | string,
    mailboxId: Number | string,
    title?: string | null,
  ): any {
    const titleVal: string = title ? title : ''

    return {
      name: routerNames.MAILBOX_SETTINGS_OFFICE_HOURS,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
        title: titleVal,
      },
      query: {},
    }
  }

  public getMailboxSettingsOutgoingEmailRoute(
    accountId: Number | string,
    mailboxId: Number | string,
    title?: string | null,
  ): any {
    const titleVal: string = title ? title : ''

    return {
      name: routerNames.MAILBOX_SETTINGS_OUTGOING_EMAIL,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
        title: titleVal,
      },
      query: {},
    }
  }

  public getMailboxSettingsTicketPortalRoute(
    accountId: Number | string,
    mailboxId: Number | string,
    title?: string | null,
  ): any {
    const titleVal: string = title ? title : ''

    return {
      name: routerNames.MAILBOX_SETTINGS_TICKET_PORTAL,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
        title: titleVal,
      },
      query: {},
    }
  }

  public getMailboxSettingsConversationRulesRoute(
    accountId: Number | string,
    mailboxId: Number | string,
    title?: string | null,
  ): any {
    const titleVal: string = title ? title : ''

    return {
      name: routerNames.MAILBOX_SETTINGS_CONVERSATION_RULES,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
        title: titleVal,
      },
      query: {},
    }
  }

  public getMailboxSettingsChecklistTemplateRoute(
    accountId: Number | string,
    projectId: Number | string,
  ): any {
    return {
      name: routerNames.MAILBOX_SETTINGS_CHECKLIST_TEMPLATES,
      params: {
        accountId: accountId.toString(),
        id: projectId.toString(),
      },
      query: {},
    }
  }

  public getMailboxSettingsAssignPeopleRoute(
    accountId: Number | string,
    mailboxId: Number | string,
    title?: string | null,
  ): any {
    const titleVal: string = title ? title : ''

    return {
      name: routerNames.MAILBOX_SETTINGS_ASSIGN_PEOPLE,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
        title: titleVal,
      },
      query: {},
    }
  }

  public getProjectSettingsAssignPeopleRoute(
    accountId: Number | string,
    projectId: Number | string,
    title?: string | null,
  ): any {
    const titleVal: string = title ? title : ''

    return {
      name: routerNames.PROJECT_SETTINGS_ASSIGN_PEOPLE,
      params: {
        accountId: accountId.toString(),
        id: projectId.toString(),
        title: titleVal,
      },
      query: {},
    }
  }

  /* Returns project landing route based on defaulted view (listing or KB) */
  public getProjectLandingRoute(
    accountId: number | string,
    projectId: number | string,
    permissions: Permissions,
    title?: string | null,
    defaultFilterId?: number | string | null,
  ) {
    if (LocalStore.isProjectDefaultedToKanban(projectId)) {
      return this.getProjectKanbanRoute(
        accountId,
        projectId,
        permissions,
        title,
        defaultFilterId,
      )
    }
    return this.getProjectListingRoute(
      accountId,
      projectId,
      permissions,
      title,
      defaultFilterId,
    )
  }

  /* Returns project listing route based on if project is sample */
  public getProjectListingRoute(
    accountId: Number | string,
    projectId: Number | string,
    permissions: Permissions,
    title?: string | null,
    defaultFilterId?: number | string | null,
    customFilterId?: number | string | null,
  ): any {
    const routeName: string = permissions.isSampleInternalProjectID(projectId)
      ? routerNames.SAMPLE_PROJECT_LISTING
      : routerNames.PROJECT_LISTING

    const query: any = defaultFilterId
      ? {
          default_filter_id: defaultFilterId,
        }
      : customFilterId
        ? {
            custom_filter_id: customFilterId,
          }
        : {}

    return {
      name: routeName,
      params: {
        accountId: accountId.toString(),
        id: projectId.toString(),
        title: title ? title : '',
      },
      query,
    }
  }

  /* Returns project kanban route based on if project is sample */
  public getProjectKanbanRoute(
    accountId: Number | string,
    projectId: Number | string,
    permissions: Permissions,
    title?: string | null,
    defaultFilterId?: number | string | null,
    customFilterId?: number | string | null,
  ): any {
    const routeName = permissions.isSampleInternalProjectID(projectId)
      ? routerNames.SAMPLE_PROJECT_KANBAN
      : routerNames.PROJECT_KANBAN

    const query: any = defaultFilterId
      ? {
          default_filter_id: defaultFilterId,
        }
      : customFilterId
        ? {
            custom_filter_id: customFilterId,
          }
        : {}

    return {
      name: routeName,
      params: {
        accountId: accountId.toString(),
        id: projectId.toString(),
        title: title ? title : '',
      },
      query,
    }
  }

  public getProjectSettingsChecklistTemplateRoute(
    accountId: Number | string,
    projectId: Number | string,
  ): any {
    return {
      name: routerNames.PROJECT_SETTINGS_CHECKLIST_TEMPLATES,
      params: {
        accountId: accountId.toString(),
        id: projectId.toString(),
      },
      query: {},
    }
  }

  public getConversationRoute(
    accountId: Number | string,
    mailboxId: Number | string,
    conversationId: Number | string,
    mailboxName: string | null,
    refNumber?: Number | string | null,
    title?: string | null,
    eventId?: Number | string | null,
    fromAll?: boolean | string | null,
    fromMailboxHome?: boolean | string | null,
  ): any {
    let titleVal: string = ''

    if (refNumber && title) {
      titleVal = `#${refNumber}: ${title}`
    }

    const route: any = {
      name: routerNames.CONVERSATION_DETAIL,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
        childId: conversationId.toString(),
        title: titleVal,
        refNumber,
        mailboxName,
        from_all: fromAll,
        from_entity_home: fromMailboxHome,
      },
    }

    if (eventId) {
      route.hash = '#e' + eventId
    }

    return route
  }

  public getTaskRoute(
    accountId: Number | string,
    projectId: Number | string,
    taskId: Number | string,
    permissions: Permissions,
    projectName?: string | null,
    refNumber?: Number | string | null,
    title?: string | null,
    eventId?: Number | string | null,
    fromAll?: boolean | string | null,
    fromListing?: boolean | string | null,
    fromKanbanBoard?: boolean | string | null,
  ): any {
    let titleVal: string = ''

    if (refNumber && title) {
      titleVal = `#${refNumber}: ${title}`
    }

    const name: string = permissions.isSampleInternalProjectID(projectId)
      ? routerNames.SAMPLE_TASK_DETAIL
      : routerNames.TASK_DETAIL

    const route: any = {
      name,
      params: {
        accountId: accountId.toString(),
        id: projectId.toString(),
        childId: taskId.toString(),
        title: titleVal,
        refNumber,
        projectName,
        from_all: fromAll,
        from_entity_home: fromListing,
        from_kanban_board: fromKanbanBoard,
      },
    }

    if (eventId) {
      route.hash = '#e' + eventId
    }

    return route
  }

  public getWorkflowsRoute(accountId: Number | string): any {
    return {
      name: routerNames.WORKFLOW_HOME,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getWorkflowDetailRoute(
    accountId: Number | string,
    workflowId: Number | string,
  ): any {
    return {
      name: routerNames.WORKFLOW_DETAIL,
      params: {
        accountId: accountId.toString(),
        id: workflowId.toString(),
      },
    }
  }

  public getCalendarRoute(
    accountId: Number | string,
    entityId?: Number | string,
  ): any {
    if (entityId) {
      return {
        name: routerNames.CALENDAR,
        params: {
          accountId: accountId.toString(),
        },
        query: {
          entity_id: entityId,
        },
      }
    }
    return {
      name: routerNames.CALENDAR,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getPeopleRoute(accountId: Number | string): any {
    return {
      name: routerNames.PEOPLE,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getPersonDetailRoute(
    accountId: Number | string,
    accountMembershipId: Number | string,
  ): any {
    return {
      name: routerNames.PERSON_DETAIL,
      params: {
        accountId: accountId.toString(),
        accountMembershipId: accountMembershipId.toString(),
      },
    }
  }

  public getGuestsRoute(accountId: Number | string): any {
    return {
      name: routerNames.GUESTS,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getGuestsDetailRoute(
    accountId: Number | string,
    accountMembershipId: Number | string,
  ): any {
    return {
      name: routerNames.GUESTS_DETAIL,
      params: {
        accountId: accountId.toString(),
        accountMembershipId: accountMembershipId.toString(),
      },
    }
  }

  public getCompanyDetailRoute(
    accountId: Number | string,
    companyId: Number | string,
  ): any {
    return {
      name: routerNames.COMPANY_DETAIL,
      params: {
        accountId: accountId.toString(),
        companyId: companyId.toString(),
      },
    }
  }

  public getCompanyGuestsDetailRoute(
    accountId: Number | string,
    companyId: Number | string,
  ): any {
    return {
      name: routerNames.COMPANY_GUESTS_DETAIL,
      params: {
        accountId: accountId.toString(),
        companyId: companyId.toString(),
      },
    }
  }

  public getCreateProjectRoute(
    accountId: Number | string,
    workflowId?: Number | string,
  ): any {
    return {
      name: routerNames.PROJECT_NEW,
      params: { accountId: accountId.toString() },
      query: {
        workflow_id: workflowId,
      },
    }
  }

  public getCreateMailboxRoute(
    accountId: Number | string,
    workflowId?: Number | string,
  ): any {
    return {
      name: routerNames.MAILBOX_NEW,
      params: { accountId: accountId.toString() },
      query: {
        workflow_id: workflowId,
      },
    }
  }

  public getGlobalCreateTaskRoute(accountId: Number | string): any {
    return {
      name: routerNames.GLOBAL_TASK_NEW,
      params: { accountId: accountId.toString() },
    }
  }

  public getMailboxVerificationIntroRoute(
    accountId: Number | string,
    mailboxId: Number | string,
  ): any {
    return {
      name: routerNames.MAILBOX_VERIFICATION_INTRO,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
    }
  }

  public getMailboxVerificationAutoForwardingRoute(
    accountId: Number | string,
    mailboxId: Number | string,
  ): any {
    return {
      name: routerNames.MAILBOX_VERIFICATION_AUTO_FORWARDING,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
    }
  }

  public getMailboxVerificationAutoForwardingTestingRoute(
    accountId: Number | string,
    mailboxId: Number | string,
  ): any {
    return {
      name: routerNames.MAILBOX_VERIFICATION_AUTO_FORWARDING_TESTING,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
    }
  }

  public getMailboxVerificationOutgoingEmailRoute(
    accountId: Number | string,
    mailboxId: Number | string,
  ): any {
    return {
      name: routerNames.MAILBOX_VERIFICATION_OUTGOING_EMAIL,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
    }
  }

  public getCreatePeopleRoute(
    accountId: Number | string,
    companyId?: Number | string,
  ): any {
    if (companyId) {
      return {
        name: routerNames.COMPANY_PEOPLE_CREATE,
        params: {
          accountId: accountId.toString(),
          companyId: companyId.toString(),
        },
      }
    }

    return {
      name: routerNames.PEOPLE_CREATE,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getCreateGuestsRoute(
    accountId: Number | string,
    companyId?: Number | string,
  ): any {
    if (companyId) {
      // TODO: fix this
      return {
        name: routerNames.COMPANY_PEOPLE_CREATE,
        params: {
          accountId: accountId.toString(),
          companyId: companyId.toString(),
        },
      }
    }

    return {
      name: routerNames.GUESTS_CREATE,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getCreateCompanyRoute(accountId: Number | string): any {
    return {
      name: routerNames.COMPANY_CREATE,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getCreateGuestsCompanyRoute(accountId: Number | string): any {
    return {
      name: routerNames.COMPANY_GUESTS_CREATE,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getAccountRegistrationCompleteRoute(
    donedoneToken: string,
    gAuthToken: string,
    gl: string,
  ): any {
    return {
      name: routerNames.ACCOUNT_REGISTRATION_COMPLETE,
      query: {
        donedone_token: donedoneToken,
        gauth_token: gAuthToken,
        _gl: gl,
      },
    }
  }

  public getDevelopersHomeRoute(accountId: Number | string): any {
    return {
      name: routerNames.DEVELOPERS_HOME,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getOutboundWebhooksRoute(accountId: Number | string): any {
    return {
      name: routerNames.DEVELOPERS_OUTBOUND_WEBHOOKS,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getTicketPortalListingRoute(
    accountId: Number | string,
    mailboxId: Number | string,
  ): any {
    return {
      name: routerNames.TICKET_PORTAL_LISTING,
      params: {
        accountId,
        mailboxId,
      },
    }
  }

  public getTicketPortalDetailRoute(
    accountId: Number | string,
    mailboxId: Number | string,
    ticketId: Number | string,
  ): any {
    return {
      name: routerNames.TICKET_PORTAL_DETAIL,
      params: {
        accountId,
        mailboxId,
        ticketId,
      },
    }
  }

  public getTicketPortalProfileRoute(): any {
    return {
      name: routerNames.TICKET_PORTAL_PROFILE,
    }
  }

  /* Non-app or direct app URLs */
  public getReportsUrl(
    accountId: Number | string,
    entityId?: Number | string,
  ): any {
    if (entityId) {
      return `${
        process.env.VUE_APP_SERVER_SIDE_APP_URL
      }/app/${accountId}/reports?entity_id=${entityId}`
    }
    return `${process.env.VUE_APP_SERVER_SIDE_APP_URL}/app/${accountId}/reports`
  }

  public getProfileUrl(accountId: Number | string): any {
    return `${
      process.env.VUE_APP_SERVER_SIDE_APP_URL
    }/app/${accountId}/account/profile`
  }

  public getAPIKeyUrl(accountId: Number | string): any {
    return `${
      process.env.VUE_APP_SERVER_SIDE_APP_URL
    }/app/${accountId}/account/api-key`
  }

  public getAccountSettingsUrl(accountId: Number | string): any {
    return `${
      process.env.VUE_APP_SERVER_SIDE_APP_URL
    }/app/${accountId}/account/settings`
  }

  public getBillingInformationUrl(accountId: Number | string): any {
    return `${
      process.env.VUE_APP_SERVER_SIDE_APP_URL
    }/app/${accountId}/account/billing-information`
  }

  public getAccountPickerUrl(): any {
    return `${process.env.VUE_APP_SERVER_SIDE_APP_URL}/account-picker`
  }

  public getSignupUrl(): any {
    return `${process.env.VUE_APP_SERVER_SIDE_APP_URL}/signup`
  }

  public getLoginUrl(): any {
    return `${process.env.VUE_APP_SERVER_SIDE_APP_URL}/security/login`
  }

  public getBaseUrl(): any {
    return `${process.env.VUE_APP_SERVER_SIDE_APP_URL}`
  }

  public getReissueUserRegistrationEmailUrl(oldToken: string): any {
    return `${
      process.env.VUE_APP_SERVER_SIDE_APP_URL
    }/signup/registration/re-issue-email?old_token=${oldToken}`
  }

  public getWorkflowsUrl(accountId: Number | string): any {
    return `${
      process.env.VUE_APP_SERVER_SIDE_APP_URL
    }/app/${accountId}/workflows`
  }

  public getWorkflowsCreateUrl(accountId: Number | string): any {
    return `${
      process.env.VUE_APP_SERVER_SIDE_APP_URL
    }/app/${accountId}/workflows/create`
  }

  public getMailboxSetupAutoForwardingUrl(
    accountId: Number | string,
    mailboxId: Number | string,
  ): any {
    return `${
      process.env.VUE_APP_URL
    }/${accountId}/mailbox/${mailboxId}/setup/auto-forwarding`
  }

  public getProjectSetupAssignPeopleUrl(
    accountId: Number | string,
    projectId: Number | string,
  ): any {
    return `${
      process.env.VUE_APP_URL
    }/${accountId}/project/${projectId}/setup/assign-people`
  }

  public getRegistrationAccountAddMembersUrl(
    accountId: Number | string,
    email: string,
  ): any {
    return `${
      process.env.VUE_APP_URL
    }/${accountId}/registration/add-members?email=${email}`
  }
}()
